import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ClipboardList, Plus, ArrowRight, GraduationCap, BookOpen, Clock, CheckCircle2, PlayCircle } from "lucide-react";
import { useAllLearningAssignments } from "@/hooks/useLearningAssignments";

export function LearningAssignmentsWidget() {
  const navigate = useNavigate();
  const { data: assignments, isLoading, error } = useAllLearningAssignments();

  // Calculate stats
  const stats = {
    total: assignments?.length || 0,
    curricula: assignments?.filter(a => a.type === 'curriculum').length || 0,
    courses: assignments?.filter(a => a.type === 'course').length || 0,
    notStarted: assignments?.filter(a => a.status === 'not_started').length || 0,
    inProgress: assignments?.filter(a => a.status === 'in_progress').length || 0,
    completed: assignments?.filter(a => a.status === 'completed').length || 0,
  };

  // Get most recent assignments (last 3)
  const recentAssignments = assignments?.slice(0, 3) || [];

  const handleNavigate = (path: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(path);
  };

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-primary" />
              Learning Assignments
            </CardTitle>
            <CardDescription>Manage user assignments</CardDescription>
          </div>
          <Button 
            size="sm" 
            onClick={handleNavigate("/admin/assignments")}
          >
            <Plus className="h-4 w-4 mr-1" />
            New
          </Button>
        </div>
      </CardHeader>

      <CardContent className="flex-1">
        {isLoading ? (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
            </div>
            <Skeleton className="h-4 w-24" />
            <div className="space-y-2">
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-4 text-destructive text-sm">
            Error loading assignments
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              <button
                onClick={handleNavigate("/admin/assignments?status=not_started")}
                className="p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors text-center cursor-pointer"
              >
                <Clock className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
                <p className="text-lg font-bold">{stats.notStarted}</p>
                <p className="text-xs text-muted-foreground">Not Started</p>
              </button>
              <button
                onClick={handleNavigate("/admin/assignments?status=in_progress")}
                className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors text-center cursor-pointer"
              >
                <PlayCircle className="h-4 w-4 mx-auto mb-1 text-blue-600 dark:text-blue-400" />
                <p className="text-lg font-bold text-blue-700 dark:text-blue-300">{stats.inProgress}</p>
                <p className="text-xs text-blue-600 dark:text-blue-400">In Progress</p>
              </button>
              <button
                onClick={handleNavigate("/admin/assignments?status=completed")}
                className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors text-center cursor-pointer"
              >
                <CheckCircle2 className="h-4 w-4 mx-auto mb-1 text-green-600 dark:text-green-400" />
                <p className="text-lg font-bold text-green-700 dark:text-green-300">{stats.completed}</p>
                <p className="text-xs text-green-600 dark:text-green-400">Completed</p>
              </button>
            </div>

            {/* Type breakdown */}
            <div className="flex items-center gap-4 mb-4 text-sm">
              <div className="flex items-center gap-1">
                <GraduationCap className="h-4 w-4 text-primary" />
                <span className="font-medium">{stats.curricula}</span>
                <span className="text-muted-foreground">Curricula</span>
              </div>
              <div className="flex items-center gap-1">
                <BookOpen className="h-4 w-4 text-accent-foreground" />
                <span className="font-medium">{stats.courses}</span>
                <span className="text-muted-foreground">Courses</span>
              </div>
            </div>

            {/* Recent Assignments */}
            {stats.total === 0 ? (
              <div className="text-center py-6">
                <ClipboardList className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground mb-3">No assignments yet</p>
                <Button 
                  size="sm" 
                  onClick={handleNavigate("/admin/assignments")}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Create First Assignment
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Recent</p>
                {recentAssignments.map((assignment) => (
                  <div
                    key={assignment.id}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{assignment.user_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{assignment.item_name}</p>
                    </div>
                    <Badge 
                      variant="outline" 
                      className={
                        assignment.status === 'completed' 
                          ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300'
                          : assignment.status === 'in_progress'
                          ? 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300'
                          : ''
                      }
                    >
                      {assignment.status === 'completed' ? 'Done' : assignment.status === 'in_progress' ? 'Active' : 'Pending'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>

      <CardFooter className="pt-0">
        <Button 
          variant="outline" 
          className="w-full" 
          onClick={handleNavigate("/admin/assignments")}
        >
          View All Assignments
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </CardFooter>
    </Card>
  );
}
